import { lazy, Suspense } from "react";
import type { StaysHubMapProps } from "./stays-hub-map";

export type { StaysHubMapProps, StaysMapBounds, StaysMapPin, StaysMapView } from "./stays-hub-map";

/**
 * The map lives in its own chunk: most guests never turn it on, and the Google
 * Maps library should not ride along on every Stays page load.
 */
const loadMap = () => import("./stays-hub-map");
const StaysHubMap = lazy(loadMap);

/**
 * Warm the map before it is asked for: the chunk once the page is idle, and a
 * connection to Google's map servers, so tapping Map does not sit on a blank
 * grey screen while both download. Costs nothing if the guest never taps it.
 */
export function preloadStaysMap(): void {
  if (typeof window === "undefined" || !STAYS_MAP_AVAILABLE) return;
  for (const href of ["https://maps.googleapis.com", "https://maps.gstatic.com"]) {
    if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) continue;
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = href;
    link.crossOrigin = "";
    document.head.appendChild(link);
  }
  const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
  idle(() => void loadMap());
}

/** Same variable brand-map.ts reads; without a key there is no map to offer. */
export const STAYS_MAP_AVAILABLE = !!(import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined);

export function StaysHubMapLazy(props: StaysHubMapProps) {
  return (
    <Suspense
      fallback={
        <div className={`animate-pulse rounded-[24px] bg-[#e9ebf2] ${props.className ?? ""}`} aria-hidden />
      }
    >
      <StaysHubMap {...props} />
    </Suspense>
  );
}
